import { FileText, Send, RefreshCw } from 'lucide-react';

type MemedStatus = 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED' | 'IDLE';

interface Exam {
  id: string;
  patientName: string;
  date: string;
  exams: string[];
  memedStatus: MemedStatus;
  memedId?: string;
  pdfUrl?: string;
}

interface MemedExamCardProps {
  exam: Exam;
  onViewPdf?: () => void;
  onResend?: () => void;
  onSync?: () => void;
  isSyncing?: boolean;
  isResending?: boolean;
}

const statusConfig: Record<string, { label: string; bgClass: string; textClass: string }> = {
  SYNCED: {
    label: 'Sincronizado com Memed',
    bgClass: 'bg-green-100',
    textClass: 'text-green-700',
  },
  PENDING: {
    label: 'Enviando...',
    bgClass: 'bg-yellow-100',
    textClass: 'text-yellow-700',
  },
  FAILED: {
    label: 'Erro',
    bgClass: 'bg-red-100',
    textClass: 'text-red-700',
  },
  SIGNED: {
    label: 'Assinado',
    bgClass: 'bg-blue-100',
    textClass: 'text-blue-700',
  },
  CANCELLED: {
    label: 'Cancelado',
    bgClass: 'bg-gray-100',
    textClass: 'text-gray-600',
  },
  IDLE: {
    label: 'Não enviado',
    bgClass: 'bg-gray-100',
    textClass: 'text-gray-600',
  },
};

export function MemedExamCard({
  exam,
  onViewPdf,
  onResend,
  onSync,
  isSyncing,
  isResending,
}: MemedExamCardProps) {
  const memedConfig = statusConfig[exam.memedStatus] ?? { label: 'Não enviado', bgClass: 'bg-gray-100', textClass: 'text-gray-600' };

  return (
    <div className="rounded-xl bg-white border border-neutral-200 shadow-sm p-5 flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-500">{exam.date}</span>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${memedConfig.bgClass} ${memedConfig.textClass}`}>
          {exam.memedStatus === 'SYNCED' && '✅ '}
          {exam.memedStatus === 'PENDING' && '⏳ '}
          {exam.memedStatus === 'FAILED' && '❌ '}
          {memedConfig.label}
        </span>
      </div>

      <div className="flex-1">
        <h4 className="font-semibold text-neutral-900 text-lg mb-1">{exam.patientName}</h4>

        <div className="mt-4 space-y-2">
          <span className="text-xs text-neutral-500 uppercase tracking-wide">Exames:</span>
          {exam.exams.map((examName, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="text-sm text-neutral-700">• {examName}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-neutral-100 flex flex-col gap-2">
        {exam.memedStatus === 'SYNCED' && (
          <>
            <button
              onClick={onViewPdf}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
            >
              <FileText className="h-4 w-4" />
              Ver PDF
            </button>
            <button
              onClick={onResend}
              disabled={isResending}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              Reenviar
            </button>
          </>
        )}

        {exam.memedStatus === 'PENDING' && (
          <button
            disabled
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 rounded-lg cursor-not-allowed"
          >
            <RefreshCw className="h-4 w-4 animate-spin" />
            Processando...
          </button>
        )}

        {exam.memedStatus === 'FAILED' && (
          <button
            onClick={onSync}
            disabled={isSyncing}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            Sincronizar
          </button>
        )}

        {exam.memedStatus === 'IDLE' && (
          <button
            onClick={onSync}
            disabled={isSyncing}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {isSyncing ? 'Enviando...' : 'Enviar para Memed'}
          </button>
        )}
      </div>
    </div>
  );
}